import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, RefreshCw, Plus, CheckCircle2, Circle, Download } from "lucide-react";

const btn = "inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50";

export function LessonActions({ year, order, status, online, saved, completed, added, user, actionErr, onRegenerate, onToggleOffline, onToggleComplete, onAddPlaylist }) {
  const navigate = useNavigate();
  const prevOrder = Number(order) - 1;
  const canSave = status === "done" || status === "offline";
  const streaming = status === "streaming";
  return (
    <div className="flex flex-wrap items-center gap-3">
      {actionErr && <div className="w-full rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="lesson-action-error">{actionErr}</div>}
      <button onClick={onRegenerate} disabled={streaming || !online} title={online ? "Generate a fresh explainer" : "Reconnect to regenerate"} data-testid="lesson-regenerate" className={`${btn} border-white/15 bg-white/5 hover:bg-white/10`}>
        <RefreshCw size={14} className={streaming ? "animate-spin" : ""} /> {streaming ? "Generating…" : "Regenerate"}
      </button>
      <button onClick={onToggleOffline} disabled={!canSave && !saved} data-testid="lesson-save-offline" aria-pressed={saved} title={saved ? "Remove saved copy" : "Save this lesson on your device"}
        className={`${btn} ${saved ? "border-lime-400/50 bg-lime-500/10 text-lime-200" : "border-white/15 bg-white/5 hover:bg-white/10"}`}>
        <Download size={14} /> {saved ? "Saved offline ✓" : "Save offline"}
      </button>
      {user && (
        <button onClick={onToggleComplete} data-testid="lesson-complete" className={`${btn} ${completed ? "border-lime-400/50 bg-lime-500/10 text-lime-200 hover:bg-lime-500/15" : "border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"}`}>
          {completed ? <><CheckCircle2 size={14} /> Marked read</> : <><Circle size={14} /> Mark as read</>}
        </button>
      )}
      <button onClick={onAddPlaylist} data-testid="lesson-add-playlist" className={`${btn} border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/15`}>
        <Plus size={14} /> {added ? "Added ✓" : "Add to playlist"}
      </button>
      <Link to="/tutor" data-testid="lesson-ask-tutor" className={`${btn} border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/15`}>Ask ChemBuddy about this →</Link>
      <div className="ml-auto flex items-center gap-2">
        <button onClick={() => navigate(`/curriculum/${year}/${prevOrder}`)} disabled={prevOrder < 1} data-testid="lesson-prev" className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs disabled:opacity-40"><ArrowLeft size={12} /> Prev</button>
        <button onClick={() => navigate(`/curriculum/${year}/${Number(order) + 1}`)} data-testid="lesson-next" className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs">Next <ArrowRight size={12} /></button>
      </div>
    </div>
  );
}

export function LessonExcerpts({ excerpts }) {
  if (excerpts.length === 0) return null;
  return (
    <section className="rounded-md border border-white/10 bg-black/40 p-6" data-testid="lesson-excerpts">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300">Source excerpts</div>
        <h3 className="font-display text-lg font-semibold text-white">From <em>Chemistry for the IB Diploma 3e</em> · Talbot</h3>
      </div>
      <div className="space-y-3">
        {excerpts.map((e, i) => (
          <div key={`${e.page}-${e.text.slice(0, 24)}`} className="rounded-md border border-white/10 bg-black/60 p-4" data-testid={`excerpt-${i}`}>
            <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300">
              <span className="rounded border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-amber-200">p.{e.page}</span>
              <span className="font-mono-num text-slate-500">relevance {e.score.toFixed(1)}</span>
            </div>
            <p className="text-xs leading-relaxed text-slate-300">{e.text.length > 380 ? e.text.slice(0, 380) + "…" : e.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
